<template>
  <div class="auto-reload-container pl-5">
    <div class="auto-reload-container__play-pause-btn">
      <v-btn
        v-tooltip="autoReloadTooltip"
        text
        width="54"
        height="40"
        small
        @click="toggleReload"
      >
        <div
          class="status-ball ml-2"
          :style="statusBallStyle"
        />
        <StIcon
          :key="playOrPauseBtn"
          :icon-name="playOrPauseBtn"
          :size="40"
        />
      </v-btn>
    </div>
    <div class="auto-reload-container__force-refresh">
      <v-btn
        v-tooltip="forceRefreshTooltip"
        text
        width="36"
        height="40"
        small
        @click="forceRefresh"
      >
        <div :class="refreshClass">
          <StIcon
            :icon-name="refreshIcon"
            :size="36"
          />
        </div>
      </v-btn>
    </div>
  </div>
</template>

<script>
import StIcon from '@stilingue/st-icon';
import colors from '@/utils/colors';
import { MUTATE } from '@/store/modules/mutation_types';
import { mapMutations, mapState, mapGetters } from 'vuex';
import { sleep } from '@/utils/sleep';
import moment from 'moment';
import { fullDate } from '@/utils/date';
import { trackEventWithUserProperties } from '@/utils/mixpanel';

// 5 minutes
const DEFAULT_TIME_TO_RELOAD = 5 * 60 * 1000;

export default {
  name: 'AutoReload',
  components: {
    StIcon,
  },
  data() {
    return {
      playIcon: 'play',
      pauseIcon: 'pause',
      refreshIcon: 'refresh',
      rotateRefreshIcon: false,
      animationTotalTimeDelay: 1000,
      reloadTimer: '',
      autoReloadOffTooltip: 'Recarregamento automático desligado',
      forceRefreshTooltip: 'Atualizar dados dos componentes',
      lastReloadTimestamp: '',
    };
  },
  computed: {
    ...mapState('misc', [
      'autoReload',
    ]),
    ...mapGetters('dashboard', [
      'activeDashboard',
    ]),
    playOrPauseBtn() {
      return this.autoReload ? this.playIcon : this.pauseIcon;
    },
    statusBallStyle() {
      return {
        backgroundColor: this.statusBallColor,
      };
    },
    statusBallColor() {
      if (this.autoReload) {
        return colors.support['5'];
      }
      return colors.ui.red;
    },
    refreshClass() {
      return {
        'rotate-360': this.rotateRefreshIcon,
      };
    },
    autoReloadOnTooltip() {
      return `Última atualização ${fullDate(this.lastReloadTimestamp)}`;
    },
    autoReloadTooltip() {
      if (this.autoReload) {
        return this.autoReloadOnTooltip;
      }

      return this.autoReloadOffTooltip;
    },
  },
  created() {
    this.setReloadTime();
  },
  methods: {
    ...mapMutations('misc', {
      mutateMisc: MUTATE,
    }),
    toggleReload() {
      const newState = !this.autoReload;
      this.mutateMisc({
        property: 'autoReload',
        with: newState,
      });

      newState ? this.startReloadTimer() : this.stopReloadTimer();

      trackEventWithUserProperties('ST Dashboard LiveButton On/Off', { On: newState });
    },
    async forceRefresh() {
      this.setReloadTime();

      this.mutateMisc({
        property: 'forceRefresh',
        with: true,
      });

      await this.startRotateRefresh();

      this.mutateMisc({
        property: 'forceRefresh',
        with: false,
      });

      trackEventWithUserProperties('ST Dashboard ReloadButton Click');
    },
    async startRotateRefresh() {
      this.rotateRefreshIcon = true;
      await sleep(this.animationTotalTimeDelay);
      this.rotateRefreshIcon = false;
    },
    setReloadTime() {
      this.lastReloadTimestamp = moment().format('x');
    },
    startReloadTimer() {
      this.reloadTimer = setInterval(() => {
        this.setReloadTime();
        this.silentRefresh();
      }, this.activeDashboard.data.timeToReloadInMilliseconds || DEFAULT_TIME_TO_RELOAD);
    },
    async silentRefresh() {
      this.mutateMisc({
        property: 'silentRefresh',
        with: true,
      });

      await sleep(this.animationTotalTimeDelay);

      this.mutateMisc({
        property: 'silentRefresh',
        with: false,
      });
    },
    stopReloadTimer() {
      clearTimeout(this.reloadTimer);
    },
  },
};
</script>

<style scoped lang="scss">
  .auto-reload-container {
    display: grid;
    grid-template-columns: 50px 40px;
    column-gap: 5px;
    justify-content: center;
    align-items: center;
  }

  .status-ball {
    border-radius: 100%;
    height: 8px;
    width: 8px;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s;
  }

  .fade-enter,
  .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }

  .rotate-360 {
    animation: rotation 1s linear;
  }

  @keyframes rotation {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(359deg);
    }
  }
</style>
